import { useEffect, useState } from "react";
import { CalendarPlus, Trophy, Trash2, ChevronDown, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls } from "@/components/kit";
import { api, errMsg, fmtDate } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";

const BLANK = { type: "match", title: "", opponent: "", home_away: "home", competition: "", venue: "", starts_at: "", notes: "", is_public: true };
const TONE = { match: "volt", training: "cyan", social: "amber" };
const AV_TONE = { yes: "green", maybe: "amber", no: "red" };

const EventForm = ({ f, setF }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Type">
        <select className={inputCls} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="event-type-select">
          <option value="match">Match</option><option value="training">Training</option><option value="social">Social</option>
        </select>
      </Field>
      <Field label="Date & time"><Input type="datetime-local" required value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} data-testid="event-datetime-input" /></Field>
    </div>
    {f.type === "match" && (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Opponent"><Input required value={f.opponent} onChange={(e) => setF({ ...f, opponent: e.target.value })} data-testid="event-opponent-input" /></Field>
        <Field label="Home / away">
          <select className={inputCls} value={f.home_away} onChange={(e) => setF({ ...f, home_away: e.target.value })} data-testid="event-homeaway-select">
            <option value="home">Home</option><option value="away">Away</option><option value="neutral">Neutral</option>
          </select>
        </Field>
      </div>
    )}
    <Field label="Title" hint={f.type === "match" ? "Leave blank to use 'vs Opponent'" : ""}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-title-input" /></Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Venue"><Input value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} data-testid="event-venue-input" /></Field>
      <Field label="Competition"><Input value={f.competition} onChange={(e) => setF({ ...f, competition: e.target.value })} data-testid="event-competition-input" /></Field>
    </div>
    <Field label="Notes for players"><textarea className={`${inputCls} h-20`} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="event-notes-input" /></Field>
    <Toggle checked={f.is_public} onChange={(v) => setF({ ...f, is_public: v })} label="Show on public fan hub" testid="event-public-toggle" />
  </>
);

const EventCard = ({ e, onResult, onDelete }) => {
  const [open, setOpen] = useState(false);
  const s = e.availability_summary;
  return (
    <Panel className="transition-colors hover:border-white/20" data-testid={`event-card-${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2"><Pill tone={TONE[e.type]}>{e.type}</Pill>{e.home_away !== "neutral" && e.type === "match" && <Pill>{e.home_away}</Pill>}{e.status !== "scheduled" && <Pill tone={e.status === "completed" ? "green" : "red"}>{e.status}</Pill>}</div>
          <p className="mt-2 font-display text-2xl font-bold uppercase text-white">{e.title}</p>
          <p className="font-mono text-xs text-slate-400">{fmtDate(e.starts_at)} {e.venue && `· ${e.venue}`} {e.competition && `· ${e.competition}`}</p>
        </div>
        {e.status === "completed" && <p className="font-display text-4xl font-black text-white" data-testid={`event-score-${e.id}`}>{e.our_score}<span className="text-slate-600">–</span>{e.their_score}</p>}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`event-availability-toggle-${e.id}`}>
          <Pill tone="green">{s.yes} in</Pill><Pill tone="amber">{s.maybe} maybe</Pill><Pill tone="red">{s.no} out</Pill>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="flex gap-2">
          {e.type === "match" && <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => onResult(e)} data-testid={`record-result-${e.id}`}><Trophy className="mr-1 h-3.5 w-3.5" /> Result</Button>}
          <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(e)} data-testid={`delete-event-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {open && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-white/5 pt-3">
          {e.availability_list.map((a) => <Pill key={a.member_id} tone={AV_TONE[a.status]}>{a.name}</Pill>)}
          {!e.availability_list.length && <p className="text-xs text-slate-500">No responses yet.</p>}
        </div>
      )}
    </Panel>
  );
};

export default function Fixtures({ teamId }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(BLANK);
  const [result, setResult] = useState(null);

  const load = () => api.get(`/teams/${teamId}/events`).then((r) => setEvents(r.data));
  useEffect(() => {
    load();
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      await api.post(`/teams/${teamId}/events`, f);
      toast.success("Event added");
      setOpen(false);
      setF(BLANK);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveResult = async () => {
    try {
      await api.patch(`/teams/${teamId}/events/${result.id}`, { our_score: Number(result.our), their_score: Number(result.their), status: "completed" });
      toast.success("Result saved");
      setResult(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (e) => {
    if (!window.confirm(`Delete ${e.title}?`)) return;
    await api.delete(`/teams/${teamId}/events/${e.id}`);
    load();
  };

  const nowKey = new Date().toISOString().slice(0, 16);
  const upcoming = events.filter((e) => e.status === "scheduled" && e.starts_at >= nowKey);
  const past = events.filter((e) => !upcoming.includes(e)).reverse();

  return (
    <div className="space-y-6">
      <SectionHead title="Fixtures & events" text="Matches, training and socials. Players RSVP from their app.">
        <Button className="rounded-full font-bold" onClick={() => setOpen(true)} data-testid="add-event-btn"><CalendarPlus className="mr-1 h-4 w-4" /> Add event</Button>
      </SectionHead>
      {!events.length && <Empty icon={CalendarDays} title="No events yet" text="Add your first match or training session." />}
      {upcoming.length > 0 && <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Upcoming</p>}
      <div className="grid gap-4 xl:grid-cols-2">{upcoming.map((e) => <EventCard key={e.id} e={e} onResult={(x) => setResult({ ...x, our: x.our_score ?? 0, their: x.their_score ?? 0 })} onDelete={del} />)}</div>
      {past.length > 0 && <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Past & results</p>}
      <div className="grid gap-4 xl:grid-cols-2">{past.map((e) => <EventCard key={e.id} e={e} onResult={(x) => setResult({ ...x, our: x.our_score ?? 0, their: x.their_score ?? 0 })} onDelete={del} />)}</div>
      <FormDialog open={open} onOpenChange={setOpen} title="Add event" onSubmit={create} submitLabel="Add event" testid="event-form"><EventForm f={f} setF={setF} /></FormDialog>
      <FormDialog open={!!result} onOpenChange={(o) => !o && setResult(null)} title="Record result" onSubmit={saveResult} submitLabel="Save result" testid="result-form">
        {result && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Our score"><Input type="number" min="0" value={result.our} onChange={(e) => setResult({ ...result, our: e.target.value })} data-testid="result-our-input" /></Field>
            <Field label={result.opponent || "Them"}><Input type="number" min="0" value={result.their} onChange={(e) => setResult({ ...result, their: e.target.value })} data-testid="result-their-input" /></Field>
          </div>
        )}
      </FormDialog>
    </div>
  );
}
